import {
  Button as RACButton,
  type ButtonProps as RACButtonProps,
  Link as RACLink,
  type LinkProps as RACLinkProps,
} from "react-aria-components";

// Polymorphic: when `href` is provided the component renders as a Link (RACLink). Otherwise
// it renders as a Button (RACButton). This gives any styled `*Button` variant (StandardButton,
// OutlineButton, etc.) proper link semantics when navigating — right-click → open in new tab,
// crawler-visible, accessible link role.
//
// className/children types are pinned to the button render-props shape across both branches so
// variants (which use composeRenderProps with `isPending`) don't need separate typing per branch.
// Common render props (isHovered, isPressed, isFocused, isFocusVisible, isDisabled) exist on both
// Button and Link; button-only props (`isPending`) are simply undefined at runtime when rendered
// as a link, which is correct (links don't have a pending state).
// Button-only props without the polymorphic `href` link branch — for action-only
// buttons whose props must not admit `href` (so callers can't turn them into links).
export type BaseButtonProps = RACButtonProps;

export type ButtonProps =
  // `ref` is React 19 ref-as-prop, forwarded to the underlying RAC button — the anchor an
  // overlay needs when its trigger is not the element that owns the overlay.
  | (RACButtonProps & { href?: undefined; ref?: React.Ref<HTMLButtonElement> })
  | (Omit<RACLinkProps, "className" | "children"> & {
      href: string;
      className?: RACButtonProps["className"];
      children?: RACButtonProps["children"];
    });

export function Button(props: ButtonProps) {
  if (props.href !== undefined) {
    return <RACLink {...(props as unknown as RACLinkProps)} />;
  }
  return <RACButton {...props} />;
}
